/**
 * Live-preview rail (P4.T11) — fills the placeholder from P4.T1.
 *
 * Chat-only preview of the saved persona bundle's /simulate response,
 * anchored in the right column of the Workshop page.
 *
 * Trigger semantics:
 *   - Mount with a clean, error-free bundle → query fires → preview renders.
 *   - Save (serverBundle.id changes) → queryKey changes → fresh fetch fires.
 *   - User edits user_message + blurs → queryKey changes → fresh fetch fires.
 *   - User clicks ↻ Refresh → refetch fires against current queryKey.
 *   - Dirty bundle (hasUnsavedChanges=true) → Refresh disabled; assistant
 *     bubble grays out; amber "Save to refresh preview" chip shows.
 *   - findings contains error-severity → no fetch; placeholder + jump
 *     button render instead (reuses T9's onJump handler).
 *   - persona_wizard feature off → no fetch; muted unavailable note.
 *     The /simulate endpoint lives in the wizard router, and a
 *     deployment can enable persona_workshop without persona_wizard
 *     (e.g. enterprise profile + delta), so the rail must degrade
 *     rather than POST into a 404. Same gate as the Workshop top
 *     bar's AI affordances.
 *
 * localStorage persistence:
 *   - Key: composer.preview-message.${personaId}
 *   - Read on mount (per-personaId dep); fallback to DEFAULT_PREVIEW_MESSAGE
 *   - Write on input blur (not on every keystroke — avoids quota churn)
 *
 * See spec docs/superpowers/specs/2026-05-22-p4-t11-live-preview-rail.md
 */
import { useEffect, useState } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { Skeleton } from '@/components/ui'
import { useFeature } from '@/hooks/useFeature'
import { useSimulatePreview } from '@/hooks/useSimulatePreview'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'
import { RenderedMessage } from '@/components/chat/RenderedMessage'

const DEFAULT_PREVIEW_MESSAGE = 'Hi! What can you help me with?'

function storageKey(personaId: string): string {
  return `composer.preview-message.${personaId}`
}

interface LivePreviewRailProps {
  personaId: string
  bundleId: string | null
  hasUnsavedChanges: boolean
  findings: Finding[]
  onJump: (location: FindingLocation) => void
  avatarUrl?: string | null
  personaName?: string
}

export function LivePreviewRail({
  personaId,
  bundleId,
  hasUnsavedChanges,
  findings,
  onJump,
  avatarUrl,
  personaName,
}: LivePreviewRailProps) {
  const [userMessage, setUserMessage] = useState<string>(DEFAULT_PREVIEW_MESSAGE)

  // Read localStorage on mount (per personaId — survives bundle saves)
  useEffect(() => {
    const stored = localStorage.getItem(storageKey(personaId))
    if (stored && stored.trim().length > 0) {
      setUserMessage(stored)
    } else {
      setUserMessage(DEFAULT_PREVIEW_MESSAGE)
    }
  }, [personaId])

  const wizardEnabled = useFeature('persona_wizard')

  const errorFindings = findings.filter((f) => f.severity === 'error')
  const hasErrors = errorFindings.length > 0

  const query = useSimulatePreview({
    bundleId,
    userMessage,
    enabled: !hasErrors && wizardEnabled,
  })

  const handleBlur = () => {
    const trimmed = userMessage.trim()
    if (trimmed.length > 0) {
      localStorage.setItem(storageKey(personaId), trimmed)
    } else {
      // Empty input on blur clears the stored override so future mounts
      // fall back to DEFAULT_PREVIEW_MESSAGE rather than re-loading the
      // stale value the user just cleared.
      localStorage.removeItem(storageKey(personaId))
    }
  }

  const refreshDisabled =
    !wizardEnabled ||
    hasUnsavedChanges ||
    hasErrors ||
    query.isFetching ||
    bundleId === null

  return (
    <aside aria-label="Live preview rail" className="h-full">
      <Card className="h-full p-4 flex flex-col gap-3">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {avatarUrl ? (
              <img src={avatarUrl} alt={personaName ?? 'Persona'} className="h-6 w-6 rounded-full object-cover" />
            ) : personaName ? (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-linear-to-br from-brand-accent to-brand-primary text-[11px] font-semibold text-white">
                {personaName.charAt(0).toUpperCase()}
              </div>
            ) : null}
            <h3 className="text-sm font-semibold">Live preview</h3>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => query.refetch()}
            disabled={refreshDisabled}
            aria-label="Refresh preview"
            title={
              hasUnsavedChanges
                ? 'Save changes before refreshing'
                : hasErrors
                  ? 'Resolve required errors first'
                  : query.isFetching
                    ? 'Generating preview…'
                    : 'Refresh preview'
            }
          >
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </header>

        <input
          type="text"
          value={userMessage}
          onChange={(e) => setUserMessage(e.target.value)}
          onBlur={handleBlur}
          aria-label="Preview user message"
          placeholder="Ask your persona…"
          className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
        />

        <div className="border-t border-border" />

        {/* Body — mutually-exclusive branches */}
        {!wizardEnabled ? (
          <div className="flex flex-col items-center justify-center text-center text-muted-foreground p-6 gap-2">
            <p className="text-sm">
              Live simulation isn&apos;t available on this deployment — it
              requires the Persona Wizard feature.
            </p>
          </div>
        ) : hasErrors ? (
          <div className="flex flex-col items-center justify-center text-center p-6 gap-3">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium">
              Resolve {errorFindings.length} blocking issue
              {errorFindings.length === 1 ? '' : 's'} first.
            </p>
            <Button
              variant="secondary"
              onClick={() => onJump(errorFindings[0].location)}
            >
              Go to first error
            </Button>
          </div>
        ) : query.isLoading && !query.data ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-3/4 rounded-2xl ml-auto" />
            <Skeleton className="h-20 w-5/6 rounded-2xl" />
            <p className="text-xs text-muted-foreground text-center">
              Generating preview…
            </p>
          </div>
        ) : query.isError ? (
          <Alert variant="destructive">
            <div className="text-sm">
              {query.error?.message ?? 'Preview failed.'}
            </div>
            <Button
              variant="secondary"
              disabled={hasUnsavedChanges}
              onClick={() => query.refetch()}
            >
              Retry
            </Button>
          </Alert>
        ) : query.data ? (
          <div className="space-y-3">
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl px-3 py-2 bg-muted text-sm">
                {userMessage.trim()}
              </div>
            </div>
            <div
              className={`flex justify-start ${hasUnsavedChanges ? 'opacity-60' : ''}`}
              data-testid="assistant-bubble"
            >
              <div className="max-w-[85%] rounded-2xl px-3 py-2 bg-primary/10 text-sm">
                <RenderedMessage content={query.data.assistant_reply} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              ≈ {query.data.token_estimate.toLocaleString()} tokens
            </p>
            {hasUnsavedChanges && (
              <div className="text-xs text-warning-strong text-center font-medium">
                ⚠ Save to refresh preview
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center text-muted-foreground p-6 gap-2">
            <p className="text-sm">Start editing to see your preview here.</p>
          </div>
        )}
      </Card>
    </aside>
  )
}
